import { Suspense, useEffect, useRef, useState, type RefObject } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Float, ContactShadows } from "@react-three/drei";
import { Animal3DModel, type AnimalType } from "./Animal3DModel";
import { useDressupStore, type HeadwearType, type OutfitType, type AccessoryType, type ColorTintType } from "../../store/dressup";

type Props = {
  animalType?: AnimalType;
  height?: number | string;
  allowOrbit?: boolean;
  autoWave?: boolean;
  walking?: boolean;
  walkSpeed?: number;
  className?: string;
  headwear?: HeadwearType;
  outfit?: OutfitType;
  accessory?: AccessoryType;
  colorTint?: ColorTintType;
  onJump?: () => void;
  /**
   * 軽量描画（マップ上の駒用）。影なし・解像度上限 1.5・必要な時だけ描く（frameloop="demand"）。
   * 待機中は描き直さず、ジャンプ中だけ毎フレーム描く。画面外・タブ非表示の間は描かない。
   */
  lite?: boolean;
  /** false なら下端のヒント札を出さない（呼び出し側で Animal3DHintBadge を別の位置に置く時） */
  hint?: boolean;
};

/** 「タップでジャンプ／ドラッグで回転」のヒント札。位置は className で指定する */
export function Animal3DHintBadge({ className = "", blur = true }: { className?: string; blur?: boolean }) {
  return (
    <div className={`pointer-events-none bg-white/85 ${blur ? "backdrop-blur-sm " : ""}rounded-full px-2.5 py-0.5 text-[10px] text-stone-600 font-bold border border-[#d9b45f]/30 shadow-sm whitespace-nowrap ${className}`}>
      タップでジャンプ 🦘 / ドラッグで回転 🔄
    </div>
  );
}

/** 要素が画面内にあり、タブが表示中かどうか */
function useOnScreen(ref: RefObject<HTMLElement | null>): boolean {
  const [inView, setInView] = useState(true);
  const [visible, setVisible] = useState(() => typeof document === "undefined" || !document.hidden);
  useEffect(() => {
    const onVis = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return inView && visible;
}

/** frameloop="demand" の Canvas に描画を依頼する。ジャンプ中だけ毎フレーム描く */
function LiteFrameDriver({ active, jumping }: { active: boolean; jumping: boolean }) {
  const invalidate = useThree((s) => s.invalidate);
  useEffect(() => {
    if (active) invalidate();
  }, [active, jumping, invalidate]);
  useFrame(() => {
    if (active && jumping) invalidate();
  });
  return null;
}

export function Animal3DCanvas({
  animalType: propAnimalType,
  height = 320,
  allowOrbit = true,
  autoWave = false,
  walking = false,
  walkSpeed = 10,
  className = "",
  headwear: propHeadwear,
  outfit: propOutfit,
  accessory: propAccessory,
  colorTint: propColorTint,
  onJump,
  lite = false,
  hint = true,
}: Props) {
  const [jumping, setJumping] = useState(false);
  const [waving, setWaving] = useState(autoWave);

  // グローバルな着せ替え状態からフォールバック
  const store = useDressupStore();
  const animalType = propAnimalType ?? store.animalType;
  const headwear = propHeadwear ?? store.headwear;
  const outfit = propOutfit ?? store.outfit;
  const accessory = propAccessory ?? store.accessory;
  const colorTint = propColorTint ?? store.colorTint;

  const wrapRef = useRef<HTMLDivElement>(null);
  const onScreen = useOnScreen(wrapRef);

  function triggerJump() {
    setJumping(true);
    onJump?.();
  }

  return (
    <div
      ref={wrapRef}
      onClick={triggerJump}
      onPointerEnter={() => setWaving(true)}
      onPointerLeave={() => !autoWave && setWaving(false)}
      className={`relative w-full cursor-pointer select-none ${className}`}
      style={{ height }}
      title="クリックでジャンプ！ドラッグで360度回転できるよ🐾"
    >
      <Canvas
        shadows={!lite}
        frameloop={lite ? "demand" : "always"}
        dpr={lite ? [1, 1.5] : undefined}
        camera={{ position: [0, 1.2, 3.2], fov: 42 }}
        style={{ background: "transparent" }}
      >
        {/* 温かい太陽光ライティング（どうぶつの森風Cozy光） */}
        <ambientLight intensity={1.2} />
        <directionalLight
          position={[4, 6, 4]}
          intensity={1.5}
          castShadow={!lite}
          shadow-mapSize={[1024, 1024]}
          shadow-bias={-0.0001}
        />
        {/* 背後からのやわらかなリムライト */}
        <directionalLight position={[-3, 4, -3]} intensity={0.6} color="#FFE4B5" />

        {lite && <LiteFrameDriver active={onScreen} jumping={jumping} />}

        <Suspense fallback={null}>
          <Float speed={walking ? 0 : 1.8} rotationIntensity={walking ? 0 : 0.15} floatIntensity={walking ? 0 : 0.25}>
            <Animal3DModel
              animalType={animalType}
              waving={waving}
              jumping={jumping}
              walking={walking}
              walkSpeed={walkSpeed}
              headwear={headwear}
              outfit={outfit}
              accessory={accessory}
              colorTint={colorTint}
              onJumpComplete={() => setJumping(false)}
              maxDelta={lite ? 1 / 30 : undefined}
            />
          </Float>

          {/* 床のふんわりソフトシャドウ（lite では描かない） */}
          {!lite && (
            <ContactShadows
              position={[0, -0.65, 0]}
              opacity={0.45}
              scale={4}
              blur={2.2}
              far={3}
              color="#3E2723"
            />
          )}
        </Suspense>

        {allowOrbit && (
          <OrbitControls
            enableZoom={false}
            enablePan={false}
            minPolarAngle={Math.PI / 3}
            maxPolarAngle={Math.PI / 1.8}
          />
        )}
      </Canvas>

      {/* タップ促しヒントバッジ */}
      {hint && <Animal3DHintBadge className="absolute bottom-2 left-1/2 -translate-x-1/2" blur={!lite} />}
    </div>
  );
}
